import { Observable, catchError } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ReportFormRuleTemplate } from '@navstation-portal/reporting/data-model/ui';
import { ApiErrorMapper, BASE_API_URL } from '@navstation-portal/shared/util/api';
import { ReportDebugApiService } from './report-debug-dependencies';

/**
 * DEV-ONLY form-rule template debug. Delete this file, its provider in
 * `app.config.ts` of `reporting-v2`, and the debug routes in TVA-API to remove
 * the template debug UI.
 *
 * The two routes live behind `/debug/` in TVA-API. The merge route takes the
 * vessel, so it can apply the Vessel Form Rule Override. The merge runs on the
 * server, so the debug UI shows the merge a report gets.
 */
@Injectable({ providedIn: 'root' })
export class ReportDebugService implements ReportDebugApiService {
  private http = inject(HttpClient);
  private readonly baseUrl = inject(BASE_API_URL);
  private apiError = inject(ApiErrorMapper).forFeature('FormRuleDebug');

  getFormRuleTemplateNames(): Observable<string[]> {
    return this.http
      .get<string[]>(`${this.baseUrl}/debug/reportFormRuleTemplates`)
      .pipe(catchError(this.apiError.toApiError('getting the form rule template names')));
  }

  getMergedFormRuleTemplate(
    names: string[],
    vesselId: string,
    applyVesselOverride: boolean
  ): Observable<ReportFormRuleTemplate> {
    // Repeated 'templates' parameters, in the order they apply. The server
    // keeps that order, so the last one wins as it does for a vessel.
    const params = names.reduce(
      (carry, name) => carry.append('templates', name),
      new HttpParams().set('vesselId', vesselId).set('applyVesselOverride', String(applyVesselOverride))
    );

    return this.http
      .get<ReportFormRuleTemplate>(`${this.baseUrl}/debug/reportFormRuleTemplate`, { params })
      .pipe(catchError(this.apiError.toApiError('merging the form rule templates')));
  }
}
